import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useBooks, useDeleteBook, useResumeBook } from '../hooks/useBooks';
import type { Book } from '../hooks/useBooks';
import { Navbar } from '../components/layout/Navbar';
import { useEffect, useState } from 'react';
import { Input } from '../components/ui/input';
import { Eye, RotateCw, Search, Trash2 } from 'lucide-react';
import { useUserProfile, isActiveSubscriber } from '../hooks/useUserProfile';
import { UpgradeModal } from '../components/ui/UpgradeModal';
import { ContinueBookModal } from '../components/ui/ContinueBookModal';

function needsContinuation(book: { status: string; chaptersComplete: number; chaptersTotal: number }) {
  // Only show "Continue Book" for free-tier books that stopped after chapter 1.
  // Paid/upgraded users' books — whether generating or fully complete — should
  // never show this overlay.
  return book.status === 'complete' && book.chaptersComplete === 1 && book.chaptersTotal > 1;
}

// Firestore Timestamps serialize as { _seconds, _nanoseconds } from admin SDK,
// { seconds, nanoseconds } from client SDK, or ISO strings/numbers in other
// paths. Be tolerant of all of them.
function timestampToMillis(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const t = Date.parse(ts);
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof ts === 'object') {
    const t = ts as { _seconds?: number; seconds?: number };
    const secs = t._seconds ?? t.seconds;
    if (typeof secs === 'number') return secs * 1000;
  }
  return 0;
}

const STUCK_WRITING_MS = 5 * 60 * 1000;

// "Stuck" = has remaining chapters AND either errored, or claims to be writing
// but hasn't updated in >5 min. Server-side enforces the same threshold via a
// transactional check, so showing the button on a borderline-fresh book is
// safe — the server will reject with 409 and the watchdog will retry later.
function isStuck(book: Book): boolean {
  const hasRemaining = book.chaptersTotal > 0 && book.chaptersComplete < book.chaptersTotal;
  if (!hasRemaining) return false;
  if (book.status === 'error') return true;
  if (book.status !== 'writing') return false;
  const updatedMs = timestampToMillis(book.updatedAt);
  if (updatedMs === 0) return false;
  return Date.now() - updatedMs > STUCK_WRITING_MS;
}

export function Dashboard() {
  const { data: books, isLoading: booksLoading } = useBooks();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const isPaidUser = isActiveSubscriber(profile);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [continueBook, setContinueBook] = useState<Book | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const { mutate: deleteBook, isPending: isDeleting } = useDeleteBook();
  const { mutate: resumeBook, isPending: isResuming, variables: resumingId } = useResumeBook();

  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      setShowBanner(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('checkout');
      newParams.delete('type');
      navigate(
        { pathname: '/dashboard', search: newParams.toString() },
        { replace: true }
      );
      const timer = setTimeout(() => setShowBanner(false), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const hasBooks = !booksLoading && (books?.length ?? 0) > 0;
  const filteredBooks = hasBooks
    ? (books ?? []).filter((b) =>
        b.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />

      {showBanner && (
        <div className="bg-green-900/40 border-b border-green-700 text-green-300 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium">
            🎉 Welcome to StoryMint! Your subscription is active and your credits are ready.
          </span>
          <button
            onClick={() => setShowBanner(false)}
            className="ml-4 text-green-400 hover:text-green-200 transition-colors text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">My Books</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                // Wait for both entitlement queries before gating, so paid users
                // never hit the upgrade modal due to a transient false-negative.
                // If still loading, navigate to /books/new which also guards itself.
                if (!profileLoading && !booksLoading && !isPaidUser && (books?.length ?? 0) >= 1) {
                  setShowUpgradeModal(true);
                } else {
                  void navigate('/books/new');
                }
              }}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-xs sm:text-sm font-medium"
            >
              + New Book
            </button>
          </div>
        </div>

        {hasBooks && (
          <div className="flex justify-end mb-4 sm:mb-6">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search books..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        )}

        {booksLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : !hasBooks ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <div className="text-5xl mb-4">📖</div>
            <h2 className="text-xl font-semibold text-foreground mb-2">No books yet</h2>
            <p className="text-muted-foreground mb-6">Start your first AI-generated novel today.</p>
            <Link
              to="/books/new"
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              Create Your First Book
            </Link>
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <div className="text-4xl mb-4">🔍</div>
            <h2 className="text-lg font-semibold text-foreground mb-2">No books found</h2>
            <p className="text-muted-foreground">No books match &ldquo;{searchQuery}&rdquo;. Try a different title.</p>
          </div>
        ) : (
          <>
          {confirmingId && (
            <div
              className="fixed inset-0 z-10"
              onClick={() => setConfirmingId(null)}
            />
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredBooks.map((book) => (
              <div key={book.id} className="relative">
                <Link
                  to={`/books/${book.id}`}
                  className={`block p-5 border border-border rounded-lg bg-card transition-colors ${needsContinuation(book) ? 'pointer-events-none' : 'hover:bg-accent/50'}`}
                >
                  <div className={needsContinuation(book) ? 'blur-sm select-none' : ''}>
                    <div className="flex items-start justify-between mb-3 pr-7">
                      <h3 className="font-semibold text-foreground line-clamp-1">{book.title}</h3>
                      <span className={`ml-2 flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${
                        book.status === 'complete' ? 'bg-green-900/30 text-green-400' :
                        book.status === 'writing' ? 'bg-blue-900/30 text-blue-400' :
                        book.status === 'error' ? 'bg-red-900/30 text-red-400' :
                        'bg-yellow-900/30 text-yellow-400'
                      }`}>
                        {book.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{book.premise}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="capitalize">{book.genre}</span>
                      <span>·</span>
                      <span>{book.wordCount.toLocaleString()} words</span>
                      {book.chaptersTotal > 0 && (
                        <>
                          <span>·</span>
                          <span>
                            {isPaidUser
                              ? `${book.chaptersComplete}/${book.chaptersTotal}`
                              : `${Math.min(book.chaptersComplete, 1)}/${Math.min(book.chaptersTotal, 1)}`
                            } chapters
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>

                {needsContinuation(book) && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setContinueBook(book); }}
                      className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/50 z-10 group"
                    >
                      <span className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-full shadow-lg group-hover:bg-primary/90 transition-colors">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground animate-pulse" />
                        Continue Book
                      </span>
                    </button>
                    <Link
                      to={`/books/${book.id}/editor`}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-3 left-3 z-20 p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      aria-label="Read chapter 1"
                      title="Read chapter 1"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                  </>
                )}

                {isStuck(book) && !needsContinuation(book) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      resumeBook(book.id);
                    }}
                    disabled={isResuming && resumingId === book.id}
                    className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition-colors disabled:opacity-50"
                    title="Generation appears stuck — pick up where it left off"
                  >
                    <RotateCw className={`h-3 w-3 ${isResuming && resumingId === book.id ? 'animate-spin' : ''}`} />
                    {isResuming && resumingId === book.id ? 'Resuming…' : 'Resume'}
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setConfirmingId(confirmingId === book.id ? null : book.id);
                  }}
                  className="absolute top-3 right-3 z-20 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  aria-label="Delete book"
                >
                  <Trash2 className="h-4 w-4" />
                </button>

                {confirmingId === book.id && (
                  <div className="absolute top-9 right-3 z-20 w-52 bg-popover border border-border rounded-lg shadow-xl p-3">
                    <p className="text-xs font-semibold text-foreground mb-1">Delete this book?</p>
                    <p className="text-xs text-muted-foreground mb-3">This is permanent and cannot be undone.</p>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmingId(null); }}
                        className="px-2.5 py-1 text-xs rounded border border-border hover:bg-accent transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBook(book.id, { onSuccess: () => setConfirmingId(null) });
                        }}
                        disabled={isDeleting}
                        className="px-2.5 py-1 text-xs bg-destructive text-destructive-foreground rounded hover:bg-destructive/90 transition-colors disabled:opacity-50"
                      >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          </>
        )}
      </main>

      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      {continueBook && (
        <ContinueBookModal
          book={continueBook}
          onClose={() => setContinueBook(null)}
        />
      )}
    </div>
  );
}
